import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Button, Row, Col, ListGroup, Form, Alert, Spinner, Badge, Tabs, Tab, Card, ButtonGroup, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaThumbtack, FaInfoCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { promptsAPI } from '../../utils/httpClient';
import { useI18n } from '../../i18n';
import styled from 'styled-components';

const PromptList = styled(ListGroup)`
    max-height: 40vh;
    overflow-y: auto;
`;

const PromptEditor = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 1rem;
    background-color: #f8f9fa;
    margin-top: 1rem;
`;

const VariableStatusContainer = styled.div`
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
    border: 1px solid #dee2e6;
`;

const VariableBadge = styled(Badge)`
    margin: 2px;
    cursor: help;
`;

// 变量定义和说明
const VARIABLE_DEFINITIONS = {
    // 必须变量
    required: {
        'source_text': {
            description: 'SOURCE_TEXT_DESC',
            modules: ['translation', 'review']
        },
        'global_context': {
            description: 'GLOBAL_CONTEXT_DESC',
            modules: ['translation']
        },
        'row_context': {
            description: 'ROW_CONTEXT_DESC',
            modules: ['translation', 'review']
        },
        'terms_str': {
            description: 'TERMS_DESC',
            modules: ['translation', 'review']
        },
        'memory_str': {
            description: 'MEMORY_DESC',
            modules: ['translation', 'review']
        },
        'source_language': {
            description: 'SOURCE_LANG_DESC',
            modules: ['translation']
        },
        'target_languages': {
            description: 'TARGET_LANGS_DESC',
            modules: ['translation']
        },
        'current_translations_json': {
            description: 'CURRENT_TRANSLATIONS_DESC',
            modules: ['review']
        }
    },
    // 可选变量
    optional: {
        'game_type': {
            description: 'GAME_TYPE_DESC',
            modules: ['translation', 'review']
        },
        'text_type': {
            description: 'TEXT_TYPE_DESC',
            modules: ['translation', 'review']
        },
        'context_str': {
            description: 'CONTEXT_STR_DESC',
            modules: ['translation', 'review']
        },
        'char_limit': {
            description: 'CHAR_LIMIT_DESC',
            modules: ['translation']
        },
        'variable': {
            description: 'GENERIC_VARIABLE_DESC',
            modules: ['translation', 'review']
        }
    }
};

// API Service.
// These calls used to go out on bare axios carrying hand-built X-User-* headers and no
// Authorization at all, which worked only while the server trusted an unverified
// X-User-Id. JwtHeaderMiddleware now strips identity headers it cannot verify, so every
// one of them became a 401. Routing through promptsAPI puts them on httpClient, whose
// interceptor attaches a real bearer token (refreshing it first when needed).
const promptApiService = {
    getPrompts: async (moduleType) =>
        promptsAPI.getPrompts(moduleType ? { module_type: moduleType } : {}),

    createPrompt: async (data, isOrg) =>
        (isOrg ? promptsAPI.createOrgPrompt(data) : promptsAPI.createUserPrompt(data)),

    updatePrompt: async (promptId, data, isOrg) =>
        (isOrg
            ? promptsAPI.updateOrgPrompt(promptId, data)
            : promptsAPI.updateUserPrompt(promptId, data)),

    deletePrompt: async (promptId, isOrg) =>
        (isOrg ? promptsAPI.deleteOrgPrompt(promptId) : promptsAPI.deleteUserPrompt(promptId))
};

// 辅助函数：分析prompt中的变量
const analyzePromptVariables = (promptText, moduleType, opts = {}) => {
    if (!promptText) return { found: [], missing: [], optional: [] };

    // 提取所有 {{variable}} 格式的变量
    const variablePattern = /\{\{(\w+)\}\}/g;
    const foundVariables = [];
    let match;

    while ((match = variablePattern.exec(promptText)) !== null) {
        if (!foundVariables.includes(match[1])) {
            foundVariables.push(match[1]);
        }
    }

    // 根据模块类型确定必须和可选的变量
    const requiredVariables = [];
    const optionalVariables = [];

    // 添加必须变量
    Object.entries(VARIABLE_DEFINITIONS.required).forEach(([varName, varInfo]) => {
        if (varInfo.modules.includes(moduleType)) {
            requiredVariables.push(varName);
        }
    });

    // 添加可选变量
    Object.entries(VARIABLE_DEFINITIONS.optional).forEach(([varName, varInfo]) => {
        if (varInfo.modules.includes(moduleType)) {
            optionalVariables.push(varName);
        }
    });

    // 根据上下文动态调整必需项（当没有术语/记忆库时，terms_str/memory_str变为可选）
    const { hasTermBase = false, hasMemoryBase = false } = opts;
    if (!hasTermBase) {
        const idx = requiredVariables.indexOf('terms_str');
        if (idx !== -1) {
            requiredVariables.splice(idx, 1);
            if (!optionalVariables.includes('terms_str')) optionalVariables.push('terms_str');
        }
    }
    if (!hasMemoryBase) {
        const idx = requiredVariables.indexOf('memory_str');
        if (idx !== -1) {
            requiredVariables.splice(idx, 1);
            if (!optionalVariables.includes('memory_str')) optionalVariables.push('memory_str');
        }
    }

    // 找出缺失的必须变量
    const missingVariables = requiredVariables.filter(v => !foundVariables.includes(v));

    // 找出存在的可选变量
    const foundOptionalVariables = foundVariables.filter(v => optionalVariables.includes(v));

    return {
        found: foundVariables,
        missing: missingVariables,
        optional: foundOptionalVariables,
        required: requiredVariables.filter(v => foundVariables.includes(v))
    };
};

// 变量状态显示组件
const VariableStatus = ({ promptText, moduleType, hasTermBase = false, hasMemoryBase = false }) => {
    const { t } = useI18n();
    const analysis = analyzePromptVariables(promptText, moduleType, { hasTermBase, hasMemoryBase });

    if (!promptText) return null;

    const renderVariableBadge = (varName, status) => {
        const isRequired = VARIABLE_DEFINITIONS.required[varName];
        const isOptional = VARIABLE_DEFINITIONS.optional[varName];
        const definition = isRequired || isOptional;

        const variant = status === 'missing' ? 'danger' :
                       status === 'required' ? 'success' :
                       'info';

        const tooltipContent = definition 
            ? t(`prompts:variables.descriptions.${definition.description}`)
            : t('prompts:variables.unknownVar');

        return (
            <OverlayTrigger
                key={varName}
                placement="top"
                overlay={<Tooltip>{tooltipContent}</Tooltip>}
            >
                <VariableBadge bg={variant}>
                    {varName}
                    {status === 'missing' && ' ⚠️'}
                    {status === 'required' && ' ✓'}
                </VariableBadge>
            </OverlayTrigger>
        );
    };

    return (
        <VariableStatusContainer>
            <div className="d-flex align-items-center mb-2">
                <strong>{t('prompts:variables.analysisTitle')}</strong>
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip>{t('prompts:variables.tooltip')}</Tooltip>
                    }
                >
                    <FaInfoCircle className="ms-2 text-info" style={{ cursor: 'help' }} />
                </OverlayTrigger>
            </div>

            {analysis.missing.length > 0 && (
                <Alert variant="warning" className="py-2 mb-2">
                    <FaExclamationTriangle className="me-2" />
                    <strong>{t('prompts:variables.missingRequired')}</strong>
                    <div>
                        {analysis.missing.map(v => renderVariableBadge(v, 'missing'))}
                    </div>
                </Alert>
            )}

            <div className="mb-2">
                <small className="text-muted d-block mb-1">{t('prompts:variables.requiredPresent')}</small>
                {analysis.required.length > 0 ? (
                    analysis.required.map(v => renderVariableBadge(v, 'required'))
                ) : (
                    <span className="text-muted ms-2">{t('prompts:variables.none')}</span>
                )}
            </div>

            <div className="mb-2">
                <small className="text-muted d-block mb-1">{t('prompts:variables.optionalUsed')}</small>
                {analysis.optional.length > 0 ? (
                    analysis.optional.map(v => renderVariableBadge(v, 'optional'))
                ) : (
                    <span className="text-muted ms-2">{t('prompts:variables.none')}</span>
                )}
            </div>

            {analysis.found.filter(v =>
                !analysis.required.includes(v) &&
                !analysis.optional.includes(v) &&
                v !== 'variable'
            ).length > 0 && (
                <div>
                    <small className="text-muted d-block mb-1">{t('prompts:variables.unknownTitle')}</small>
                    {analysis.found.filter(v =>
                        !analysis.required.includes(v) &&
                        !analysis.optional.includes(v) &&
                        v !== 'variable'
                    ).map(v => renderVariableBadge(v, 'unknown'))}
                </div>
            )}
        </VariableStatusContainer>
    );
};

const PromptManagerModal = ({
    show,
    onHide,
    onSelectPrompt,
    moduleType,
    selectedPromptText,
    hasTermBase = false,
    hasMemoryBase = false
}) => {
    const [userPrompts, setUserPrompts] = useState([]);
    const [orgPrompts, setOrgPrompts] = useState([]);
    const [defaultTemplate, setDefaultTemplate] = useState(null);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('user');
    const { t } = useI18n();

    const getUserInfo = () => {
        const userInfoString = localStorage.getItem('userInfo');
        if (!userInfoString) {
            console.error("PromptManagerModal: User info not found in localStorage.");
            return { id: null, orgCode: null, level: 0 };
        }
        try {
            const parsedInfo = JSON.parse(userInfoString);
            return {
                id: parsedInfo?.id || null,
                orgCode: parsedInfo?.org_code || null,
                level: parseInt(parsedInfo?.user_level || '0')
            };
        } catch (error) {
            console.error("PromptManagerModal: Error parsing userInfo from localStorage:", error);
            return { id: null, orgCode: null, level: 0 };
        }
    };
    const userInfo = getUserInfo();

    const loadPrompts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await promptApiService.getPrompts(moduleType);
            if (response.success) {
                setUserPrompts(response.user_prompts || []);
                setOrgPrompts(response.org_prompts || []);
            } else {
                setError(response.message || t('prompts:errors.failedLoad'));
            }

            try {
                if (moduleType) {
                    const def = await promptsAPI.getDefaultTemplate(moduleType);
                    if (def && def.success && def.template) {
                        setDefaultTemplate(def.template);
                    } else {
                        setDefaultTemplate(null);
                    }
                }
            } catch (e) {
                console.warn('Failed to load backend default template:', e);
                setDefaultTemplate(null);
            }
        } catch (err) {
            setError(t('prompts:errors.serverLoad'));
        } finally {
            setLoading(false);
        }
    }, [moduleType]);

    const findMatchingPrompt = useCallback((promptText, allUserPrompts, allOrgPrompts) => {
        if (!promptText || promptText.trim() === '') return null;

        const normalizeText = (text) => text.replace(/\s+/g, ' ').trim();
        const normalizedTarget = normalizeText(promptText);

        if (defaultTemplate && defaultTemplate.prompt_text && normalizeText(defaultTemplate.prompt_text) === normalizedTarget) {
            return { prompt: defaultTemplate, isOrg: false, isDefault: true };
        }

        for (const prompt of allUserPrompts) {
            if (normalizeText(prompt.prompt_text) === normalizedTarget) {
                return { prompt, isOrg: false, isDefault: false };
            }
        }

        for (const prompt of allOrgPrompts) {
            if (normalizeText(prompt.prompt_text) === normalizedTarget) {
                return { prompt, isOrg: true, isDefault: false };
            }
        }

        return null;
    }, [moduleType, defaultTemplate]);

    useEffect(() => {
        if (show) {
            loadPrompts();
            setSelectedPrompt(null);
            setEditingPrompt(null);
            setActiveTab('user');
        }
    }, [show, loadPrompts]);

    useEffect(() => {
        if (show && selectedPromptText && userPrompts.length >= 0 && orgPrompts.length >= 0 && !loading) {
            const match = findMatchingPrompt(selectedPromptText, userPrompts, orgPrompts);
            if (match) {
                setSelectedPrompt({
                    id: match.prompt.id,
                    text: match.prompt.prompt_text,
                    name: match.prompt.name || match.prompt.prompt_name,
                    isOrg: match.isOrg,
                    isDefault: match.isDefault
                });
                setActiveTab(match.isOrg ? 'org' : 'user');
            }
        }
    }, [show, selectedPromptText, userPrompts, orgPrompts, loading, findMatchingPrompt]);

    const handleSelect = (prompt, isOrg, isDefault = false) => {
        setSelectedPrompt({
            id: prompt.id,
            text: prompt.prompt_text,
            name: prompt.name || prompt.prompt_name,
            isOrg,
            isDefault
        });
        setEditingPrompt(null);
    };

    const handleEdit = (prompt, isOrg, isDefault = false) => {
        setSelectedPrompt(null);
        if (isDefault) {
            setEditingPrompt({
                name: `Copy of ${prompt.name}`,
                text: prompt.prompt_text,
                module_type: moduleType,
                isOrg: false,
                isNew: true
            });
        } else {
            setEditingPrompt({ ...prompt, text: prompt.prompt_text, isOrg, isNew: false });
        }
    };

    const handleAddNew = (isOrg) => {
        setSelectedPrompt(null);
        setEditingPrompt({ name: '', text: '', module_type: moduleType, isOrg, isNew: true });
    };

    const handleCancelEdit = () => {
        setEditingPrompt(null);
        setError(null);
    };

    const handleSaveEdit = async () => {
        if (!editingPrompt) return;
        setError(null);

        // 检查必须变量
        const analysis = analyzePromptVariables(editingPrompt.text, moduleType);
        if (analysis.missing.length > 0) {
            const confirmSave = window.confirm(
                t('prompts:variables.missingSaveWarning', { list: analysis.missing.join(', ') })
            );
            if (!confirmSave) return;
        }

        setActionLoading(true);

        const { id, name, text, isOrg, isNew } = editingPrompt;
        const data = { name, prompt_text: text, module_type: moduleType };

        try {
            let response;
            if (isNew) {
                response = await promptApiService.createPrompt(data, isOrg);
            } else {
                response = await promptApiService.updatePrompt(id, data, isOrg);
            }

            if (response.success) {
                setEditingPrompt(null);
                loadPrompts();
            } else {
                setError(response.message || (isNew ? t('prompts:errors.createFailed') : t('prompts:errors.updateFailed')));
            }
        } catch (err) {
            setError(t('prompts:errors.saveServerError'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (prompt, isOrg) => {
        if (!window.confirm(t('prompts:confirmDelete', { name: prompt.name }))) return;
        setError(null);
        setActionLoading(true);
        try {
            const response = await promptApiService.deletePrompt(prompt.id, isOrg);
            if (response.success) {
                if (selectedPrompt?.id === prompt.id && selectedPrompt.isOrg === isOrg) {
                    setSelectedPrompt(null);
                }
                if (editingPrompt?.id === prompt.id && editingPrompt.isOrg === isOrg) {
                     setEditingPrompt(null);
                 }
                loadPrompts();
            } else {
                setError(response.message || t('prompts:errors.deleteFailed'));
            }
        } catch (err) {
            setError(t('prompts:errors.deleteServerError'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleUseSelectedPrompt = () => {
        if (selectedPrompt && onSelectPrompt) {
            // 检查变量完整性
            const analysis = analyzePromptVariables(selectedPrompt.text, moduleType);
            if (analysis.missing.length > 0) {
                const confirmUse = window.confirm(
                    t('prompts:variables.missingUseWarning', { list: analysis.missing.join(', ') })
                );
                if (!confirmUse) return;
            }

            onSelectPrompt(selectedPrompt.text, {
                id: selectedPrompt.id,
                name: selectedPrompt.name,
                isOrg: selectedPrompt.isOrg,
                isDefault: !!selectedPrompt.isDefault,
                module_type: moduleType
            });
            onHide();
        } else if (editingPrompt && !editingPrompt.isNew && onSelectPrompt) {
            const analysis = analyzePromptVariables(editingPrompt.text, moduleType);
            if (analysis.missing.length > 0) {
                const confirmUse = window.confirm(
                    `Warning: This prompt is missing required variables: ${analysis.missing.join(', ')}\n\n` +
                    `This may cause issues during translation/review. Do you still want to use this prompt?`
                );
                if (!confirmUse) return;
            }

            onSelectPrompt(editingPrompt.text, {
                id: editingPrompt.id,
                name: editingPrompt.name,
                isOrg: !!editingPrompt.isOrg,
                isDefault: false,
                module_type: moduleType
            });
            onHide();
        } else {
            alert(t('prompts:alerts.selectOrFinish'));
        }
    };

    const canManageOrgPrompts = userInfo.orgCode && userInfo.level >= 1;

    const renderPromptList = (prompts, isOrg) => {
        const currentDefault = defaultTemplate;
        const showDefault = currentDefault && !isOrg;

        return (
            <PromptList variant="flush">
                {showDefault && (
                     <ListGroup.Item
                         key={`default-${moduleType}`}
                         onClick={() => handleSelect({ ...currentDefault, id: `default-${moduleType}`, name: currentDefault.name || t('prompts:defaultTemplateName', { module: moduleType }) }, false, true)}
                         active={selectedPrompt?.id === `default-${moduleType}` && selectedPrompt.isDefault}
                         className="d-flex justify-content-between align-items-center"
                         style={{ fontStyle: 'italic', backgroundColor: '#f0f0f0', cursor: 'pointer' }}
                     >
                         <span>
                             <FaThumbtack className="me-2" /> {(currentDefault.name || t('prompts:defaultTemplateName', { module: moduleType }))} ({t('prompts:default')})
                         </span>
                         <div>
                              <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="me-1"
                                  onClick={(e) => { e.stopPropagation(); handleEdit({ ...currentDefault, name: currentDefault.name || t('prompts:defaultTemplateName', { module: moduleType }) }, false, true); }}
                                  disabled={actionLoading}
                              >
                                <FaEdit /> {t('prompts:useAsTemplate')}
                             </Button>
                         </div>
                     </ListGroup.Item>
                )}

                {prompts.length === 0 && !showDefault && <ListGroup.Item className="text-muted text-center">{t('prompts:empty')}</ListGroup.Item>}
                {prompts.map(p => {
                    const analysis = analyzePromptVariables(p.prompt_text, moduleType);
                    const hasMissingVars = analysis.missing.length > 0;

                    return (
                        <ListGroup.Item
                            key={p.id}
                            onClick={() => handleSelect(p, isOrg)}
                            active={selectedPrompt?.id === p.id && selectedPrompt.isOrg === isOrg && !selectedPrompt.isDefault}
                            className="d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer' }}
                        >
                            <span>
                                {p.name || p.prompt_name || 'Unnamed Prompt'}
                                {isOrg && <Badge bg="warning" text="dark" className="ms-2">Org</Badge>}
                                {hasMissingVars && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Missing variables: {analysis.missing.join(', ')}</Tooltip>}
                                    >
                                        <Badge bg="danger" className="ms-2">
                                            <FaExclamationTriangle />
                                        </Badge>
                                    </OverlayTrigger>
                                )}
                            </span>
                            <div>
                                {((isOrg && canManageOrgPrompts) || !isOrg) && (
                                    <>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1"
                                            onClick={(e) => { e.stopPropagation(); handleEdit(p, isOrg); }}
                                            disabled={actionLoading}
                                        >
                                            <FaEdit />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); handleDelete(p, isOrg); }}
                                            disabled={actionLoading}
                                        >
                                            <FaTrash />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </ListGroup.Item>
                    );
                })}
            </PromptList>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('prompts:title', { module: moduleType })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{typeof error === 'string' ? error : (error?.message || JSON.stringify(error) || '未知错误')}</Alert>}

                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="prompt-tabs" className="mb-3">
                    <Tab eventKey="user" title={t('prompts:tabs.my')}>
                        {loading ? (
                            <div className="text-center p-3"><Spinner animation="border" size="sm"/> {t('common:status.loading')}</div>
                        ) : (
                            <>
                                {renderPromptList(userPrompts, false)}
                                <Button variant="outline-primary" size="sm" className="mt-2" onClick={() => handleAddNew(false)} disabled={editingPrompt || actionLoading}>
                                    <FaPlus /> {t('prompts:addUser')}
                                </Button>
                            </>
                        )}
                    </Tab>
                    {userInfo.orgCode && (
                        <Tab eventKey="org" title={t('prompts:tabs.org', { org: userInfo.orgCode })}>
                            {loading ? (
                                <div className="text-center p-3"><Spinner animation="border" size="sm"/> {t('common:status.loading')}</div>
                            ) : (
                                <>
                                    {renderPromptList(orgPrompts, true)}
                                    {canManageOrgPrompts && (
                                        <Button variant="outline-warning" size="sm" className="mt-2" onClick={() => handleAddNew(true)} disabled={editingPrompt || actionLoading}>
                                            <FaPlus /> {t('prompts:addOrg')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Tab>
                    )}
                </Tabs>

                {editingPrompt && (
                    <PromptEditor>
                        <h5>
                            {editingPrompt.isNew
                                ? (editingPrompt.isOrg ? t('prompts:createOrg') : t('prompts:createUser'))
                                : t('prompts:edit', { scope: editingPrompt.isOrg ? t('prompts:org') : t('prompts:user') })
                            }
                        </h5>
                        <Form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                            <Form.Group className="mb-2">
                                <Form.Label>{t('prompts:fields.name')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingPrompt.name}
                                    onChange={(e) => setEditingPrompt({ ...editingPrompt, name: e.target.value })}
                                    required
                                    disabled={actionLoading}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>{t('prompts:fields.text')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={8}
                                    value={editingPrompt.text}
                                    onChange={(e) => setEditingPrompt({ ...editingPrompt, text: e.target.value })}
                                    required
                                    disabled={actionLoading}
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </Form.Group>

                            {/* 变量分析状态 */}
                            <VariableStatus promptText={editingPrompt.text} moduleType={moduleType} hasTermBase={hasTermBase} hasMemoryBase={hasMemoryBase} />

                            <ButtonGroup size="sm" className="mt-3">
                                <Button type="submit" variant="success" disabled={actionLoading || !editingPrompt.name || !editingPrompt.text}>
                                    {actionLoading ? <><Spinner size="sm"/> {t('common:status.loading')}</> : <><FaCheck /> {t('common:actions.save')}</>}
                                </Button>
                                <Button variant="secondary" onClick={handleCancelEdit} disabled={actionLoading}>
                                    <FaTimes /> {t('common:actions.cancel')}
                                </Button>
                            </ButtonGroup>
                        </Form>
                    </PromptEditor>
                )}

                {selectedPrompt && !editingPrompt && (
                    <Card className="mt-3">
                        <Card.Header>
                            {t('prompts:selected')}: <strong>{selectedPrompt.name}</strong>
                            {selectedPrompt.isOrg && <Badge bg="warning" text="dark" className="ms-2">{t('prompts:org')}</Badge>}
                        </Card.Header>
                        <Card.Body>
                            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '150px', overflowY: 'auto', fontSize: '0.9em' }}>
                                {selectedPrompt.text}
                            </pre>
                            {/* 变量分析状态 */}
                            <VariableStatus promptText={selectedPrompt.text} moduleType={moduleType} hasTermBase={hasTermBase} hasMemoryBase={hasMemoryBase} />
                        </Card.Body>
                    </Card>
                )}

            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('common:actions.close')}</Button>
                <Button
                    variant="primary"
                    onClick={handleUseSelectedPrompt}
                    disabled={!selectedPrompt && !(editingPrompt && !editingPrompt.isNew)}
                >
                    {t('prompts:useSelected')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PromptManagerModal;
